import { useEffect, useState, type ReactNode } from 'react';

import { BrandSplash } from '@/components/BrandSplash';
import { isSupabaseConfigured } from '@/config/env';
import { deviceId, storedReferralCode } from '@/features/referrals/referral-code';
import { usePlatform } from '@/platform/usePlatform';
import {
  ensureAnonymousSession,
  ensurePlatformSession,
  subscribeToAuthChanges,
} from '@/services/auth';
import { PlatformSessionError } from '@/services/platformSession';

import { AuthContext, type AuthState } from './AuthContext';

export function AuthProvider({ children }: { children: ReactNode }) {
  // AuthProvider живёт под PlatformProvider (src/app/providers.tsx), и это
  // единственное место, где выбор канала влияет на вход. Какая площадка —
  // здесь неизвестно и знать не нужно: есть identity или нет (ADR-041).
  const platform = usePlatform();
  const [state, setState] = useState<AuthState>(() =>
    isSupabaseConfigured
      ? { status: 'loading', session: null }
      : { status: 'unconfigured', session: null },
  );

  useEffect(() => {
    if (!isSupabaseConfigured) {
      return;
    }

    let cancelled = false;
    /*
     * Пока вход не завершён, события Supabase игнорируются: подписка
     * сразу приносит INITIAL_SESSION с тем, что лежит в хранилище
     * webview, а принадлежать оно может постороннему анонимному
     * пользователю. Приняв это за готовность, приложение успело бы
     * завести ему профиль и израсходовать стартовые кредиты устройства
     * до того, как выяснится настоящий владелец (ADR-041).
     */
    let settled = false;

    const identity = platform.getIdentity();
    const referralCode = storedReferralCode();

    const signedIn = identity
      ? ensurePlatformSession(identity, {
          deviceHash: deviceId(),
          ...(referralCode ? { referralCode } : {}),
        })
      : ensureAnonymousSession();

    signedIn
      .then((session) => {
        settled = true;
        if (!cancelled) {
          setState({ status: 'ready', session });
        }
      })
      .catch((error: unknown) => {
        settled = true;
        if (cancelled) {
          return;
        }

        /*
         * Отката в анонимную сессию нет намеренно: она создала бы ровно
         * того осиротевшего пользователя, ради устранения которого
         * ADR-041 и перевернул направление связи. Лучше честный экран
         * ошибки, чем второй аккаунт с отдельным балансом.
         */
        const message = error instanceof PlatformSessionError ? error.message : null;

        setState({
          status: 'error',
          session: null,
          ...(message ? { error: { message } } : {}),
        });
      });

    const unsubscribe = subscribeToAuthChanges((_event, session) => {
      if (!settled) {
        return;
      }

      setState((prev) => (prev.status === 'error' ? prev : { status: 'ready', session }));
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [platform]);

  return (
    <AuthContext.Provider value={state}>
      {state.status === 'loading' ? <BrandSplash /> : children}
    </AuthContext.Provider>
  );
}
